import { Link } from "react-router-dom";
import { SEOPageLayout, InlineCTA } from "../../components/seo/SEOPageLayout";

const FACTORS = [
  { title: "Brand", body: "Well-known brands like Dexcom, FreeStyle Libre, Omnipod, and Medtronic are in higher demand than lesser-known store brands and generally have a higher resale value." },
  { title: "Product type", body: "A box of continuous glucose monitor sensors is not the same as a single container of test strips. Sensor packs and pump supplies often carry more value than basic test strips." },
  { title: "Expiration date", body: "The longer the shelf life remaining, the more the product is worth. Boxes with less than six months until expiration are usually not eligible." },
  { title: "Condition", body: "Unopened, factory-sealed boxes with clean, undamaged packaging are worth more. Any tears, water damage, or missing seals reduce or eliminate the value." },
  { title: "Packaging", body: "Retail boxes with a visible barcode and expiration date are required. Loose or repackaged items generally cannot be accepted." },
  { title: "Current acceptance requirements", body: "The specific brands and quantities we can purchase change over time based on demand and supply. What we accepted last month may not be what we accept today." },
];

export default function TestStripsWorth() {
  return (
    <SEOPageLayout
      title="How Much Are Diabetic Test Strips Worth? | Jacksonville Buyer"
      description="Learn what factors affect the value of unused diabetic test strips and how to find out if your supplies are eligible in Jacksonville, FL."
      canonical="https://cashforteststripsjacksonville.com/how-much-are-diabetic-test-strips-worth/"
      breadcrumbLabel="How Much Are Diabetic Test Strips Worth"
      primaryKeyword="Diabetic Test Strip Value"
      h1="How Much Are Diabetic Test Strips Worth?"
      intro="There is no single price for a box of diabetic test strips. Value depends on several factors, and every box we review is quoted individually. This page explains what affects the value and how to get a specific figure for your supplies."
    >
      {({ openForm }) => (
        <>
          <section data-testid="section-factors">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-6" style={{ fontFamily: "Manrope" }}>
              What Affects the Value of Diabetic Test Strips?
            </h2>
            <div className="space-y-4">
              {FACTORS.map((f, i) => (
                <div key={f.title} className="bg-[#F5F5F0] rounded-xl p-5 md:p-6" data-testid={`factor-${i}`}>
                  <h3 className="font-semibold text-lg text-[#1F2937] mb-2" style={{ fontFamily: "Manrope" }}>{f.title}</h3>
                  <p className="text-[#4B5563] leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </section>

          <section data-testid="section-honest">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Why We Do Not Publish a Fixed Price List</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              The market for unused diabetic supplies changes. Publishing a fixed price list would either overpromise on days when demand is low or shortchange sellers on days when demand is high. Instead, we quote each submission individually based on what we can actually pay that week.
            </p>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              This also protects sellers from wasted trips. If your box does not qualify or the current rate is lower than you hoped, you know before you leave the house.
            </p>
          </section>

          <section data-testid="section-next-step">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>How to Get a Value for Your Supplies</h2>
            <ol className="space-y-3 text-lg text-[#4B5563] list-decimal list-inside">
              <li>Photograph the front and back of each box, showing the brand, product code, and expiration date.</li>
              <li>Text the photo to (904) 955-9646 or submit through our <Link to="/sell-diabetic-test-strips/" className="text-[#0F5132] font-semibold underline">Sell Diabetic Test Strips</Link> page.</li>
              <li>We reply with a specific figure or explain why the item does not qualify.</li>
            </ol>
          </section>

          <InlineCTA
            onGetStarted={openForm}
            headline="Contact Us to Find Out if Your Supplies Are Eligible"
            sub="Every box is different. Get an honest, specific answer for your exact supplies."
          />
        </>
      )}
    </SEOPageLayout>
  );
}
